import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import { Share, PersonAdd } from "@mui/icons-material";
import { motion } from "framer-motion";
import conferenceHeader from "../../../../../../assets/summit-conference-header.png";
import { useState, useEffect, useCallback } from "react";
import { useConferenceStream } from "./UseConferenceStream";
import {
  CountdownBadge,
  LivePreviewCard,
  SpeakerCard,
} from "./SummitConferenceUI";
import StreamViewer from "../StreamViewer";
import ShareControls from "../ShareControls";

import ConferenceSidePanel from "../ConferenceSidePanel";
import ShareDrawer from "../ShareDrawer";
import { tokens } from "../../../../../../theme";
import apiService from "../../../../../../service/Api";
import { toast } from "react-toastify";

// ─── Types ────────────────────────────────────────────────────────────────────
interface ISpeaker {
  id: number;
  name: string;
  image?: string;
  role?: string;
  email?: string;
  phone_number?: string;
}

// ─── Motion variants ──────────────────────────────────────────────────────────
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6, staggerChildren: 0.1 } },
};
const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.5 } },
};

const emptySpeakerForm = {
  name: "",
  role: "",
  email: "",
  phone_number: "",
  image: null as File | null,
};

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function ConferenceStreamPage() {
  const muiTheme = useTheme();
  const colors = tokens(muiTheme.palette.mode);
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

  const [shareDrawerOpen, setShareDrawerOpen] = useState(false);
  const [bottomTab, setBottomTab] = useState(0); // 0 = Stream Details, 1 = Program

  // ── Add Speaker modal state ──
  const [speakerDialogOpen, setSpeakerDialogOpen] = useState(false);
  const [speakerForm, setSpeakerForm] = useState(emptySpeakerForm);
  const [submittingSpeaker, setSubmittingSpeaker] = useState(false);

  // ── Speakers state ──
  const [speakers, setSpeakers] = useState<ISpeaker[]>([]);

  const {
    streams,
    activeStreamIndex,
    setActiveStreamIndex,
    currentStream,
    deceasedData,
    messages,
    streamProgram,
    isLoadingStreams,
    downloadingProgram,
    isAdmin,
    memorialTheme,
    socketRef,
    handleApprove,
    handleDelete,
    handleDownloadProgram,
  } = useConferenceStream();

  // ── Fetch speakers ──
  const fetchSpeakers = useCallback(async () => {
    try {
      const res = await apiService.get<{ results: ISpeaker[] } | ISpeaker[]>(
        "/api/individuals/event-speaker/",
      );
      const data = res.data as { results?: ISpeaker[] } | ISpeaker[];
      setSpeakers(Array.isArray(data) ? data : (data.results ?? []));
    } catch (err) {
      console.error("Failed to fetch speakers", err);
    }
  }, []);

  useEffect(() => {
    fetchSpeakers();
  }, [fetchSpeakers]);

  // ── Speaker handlers ──
  const handleSpeakerFormChange = (field: string, value: string) => {
    setSpeakerForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSpeakerImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSpeakerForm((prev) => ({ ...prev, image: file }));
  };

  const handleAddSpeaker = async () => {
    if (!speakerForm.name.trim()) {
      toast.error("Speaker name is required.");
      return;
    }
    try {
      setSubmittingSpeaker(true);
      const formData = new FormData();
      formData.append("name", speakerForm.name);
      if (speakerForm.role) formData.append("role", speakerForm.role);
      if (speakerForm.email) formData.append("email", speakerForm.email);
      if (speakerForm.phone_number)
        formData.append("phone_number", speakerForm.phone_number);
      if (speakerForm.image)
        formData.append("image", speakerForm.image, speakerForm.image.name);
      await apiService.post("/api/individuals/event-speaker/", formData);
      toast.success("Speaker added successfully!");
      setSpeakerDialogOpen(false);
      setSpeakerForm(emptySpeakerForm);
      await fetchSpeakers();
    } catch (err) {
      console.error(err);
      toast.error("Failed to add speaker.");
    } finally {
      setSubmittingSpeaker(false);
    }
  };

  const handleDeleteSpeaker = async (id: number) => {
    if (!window.confirm("Are you sure you want to remove this speaker?"))
      return;
    try {
      await apiService.delete(`/api/individuals/event-speaker/${id}/`);
      toast.success("Speaker removed successfully!");
      setSpeakers((prev) => prev.filter((sp) => sp.id !== id));
    } catch (err) {
      console.error(err);
      toast.error("Failed to remove speaker.");
    }
  };

  const handleCloseSpeakerDialog = () => {
    setSpeakerDialogOpen(false);
    setSpeakerForm(emptySpeakerForm);
  };

  // ── Loading / empty states ────────────────────────────────────────────────
  if (isLoadingStreams) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!streams.length) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Typography>No streams found for this booking.</Typography>
      </Box>
    );
  }

  // ── Derived display values from real stream data ──────────────────────────
  const eventTitle =
    currentStream?.title || currentStream?.booking?.service || "Event";

  const eventDate = currentStream?.date
    ? new Date(currentStream.date).toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

  const eventTime =
    currentStream?.start && currentStream?.end
      ? `${currentStream.start} – ${currentStream.end}`
      : "";

  const eventVenue =
    currentStream?.event_details?.venue?.name ||
    currentStream?.booking?.events?.[0]?.venue?.name ||
    "";

  const eventCity =
    currentStream?.event_details?.venue?.city ||
    currentStream?.event_details?.venue?.address_one ||
    currentStream?.event_details?.venue?.address_two ||
    "";

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: memorialTheme.surface }}>
      {/* ── Custom Conference Header ── */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: 140, md: 200 },
        }}
      >
        {/* Background image */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: { xs: 140, md: 200 },
            backgroundImage: `url(${conferenceHeader})`,
            backgroundColor: "#005CFC",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />

        {/* Content overlay */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            px: { xs: 2, md: 6 },
            pb: { xs: 2.5, md: 4 },
          }}
        >
          {/* Countdown row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1.5,
              flexWrap: "wrap",
            }}
          >
            <CountdownBadge streams={streams} />
          </Box>

          {/* Event title */}
          <Typography
            variant={isMobile ? "h5" : "h3"}
            sx={{
              color: "#fff",
              fontWeight: 800,
              lineHeight: 1.2,
              mb: 1,
              textShadow: "0 2px 8px rgba(0,0,0,0.4)",
            }}
          >
            {eventTitle}
          </Typography>

          {/* Details row */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 0.5, sm: 2 }}
            sx={{ flexWrap: "wrap" }}
          >
            {eventDate && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <CalendarTodayIcon
                  sx={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.82rem" }}
                >
                  {eventDate}
                </Typography>
              </Box>
            )}

            {(eventVenue || eventCity) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <LocationOnIcon
                  sx={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.82rem" }}
                >
                  {[eventVenue, eventCity].filter(Boolean).join(", ")}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </Box>

      <Container
        maxWidth={isMobile ? "sm" : "lg"}
        sx={{ py: { xs: 2, md: 4 } }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* ── Top event card ── */}
          <motion.div variants={itemVariants}>
            <Card
              sx={{
                mb: 3,
                borderRadius: 3,
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              }}
            >
              <CardContent>
                {/* Title row */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        color: memorialTheme.primary,
                        mb: 0.5,
                        fontWeight: 700,
                      }}
                    >
                      {eventTitle}
                    </Typography>
                    {eventDate && (
                      <Typography variant="caption" color="text.secondary">
                        {eventDate}
                      </Typography>
                    )}
                  </Box>
                  <CountdownBadge streams={streams} />
                </Box>

                {/* StreamViewer + ConferenceSidePanel */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 8 }}>
                    <StreamViewer
                      streams={streams}
                      activeStream={activeStreamIndex}
                      onStreamChange={setActiveStreamIndex}
                      memorialTheme={memorialTheme}
                      currentStream={currentStream}
                      deceasedName={deceasedData?.first_name}
                      hideTabs={false}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <ConferenceSidePanel
                      streams={streams}
                      activeStream={activeStreamIndex}
                      onStreamChange={setActiveStreamIndex}
                      memorialTheme={memorialTheme}
                      messages={messages}
                      isAdmin={isAdmin}
                      socketRef={socketRef}
                      isMobile={isMobile}
                      handleApprove={handleApprove}
                      handleDelete={handleDelete}
                      colors={colors}
                    />
                  </Grid>
                </Grid>

                {/* Download + Share */}
                {isMobile ? (
                  <Box
                    sx={{
                      mt: 2.5,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      variant="contained"
                      disabled={!streamProgram || downloadingProgram}
                      onClick={handleDownloadProgram}
                      sx={{
                        borderRadius: "50px",
                        textTransform: "none",
                        fontWeight: 600,
                        bgcolor: memorialTheme.primary,
                      }}
                    >
                      {downloadingProgram ? (
                        <CircularProgress size={18} sx={{ color: "#fff" }} />
                      ) : (
                        "Download Program"
                      )}
                    </Button>
                    <IconButton
                      onClick={() => setShareDrawerOpen(true)}
                      sx={{
                        bgcolor: "info.main",
                        color: "#fff",
                        width: 40,
                        height: 40,
                      }}
                    >
                      <Share />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ mt: 2.5 }}>
                    <ShareControls
                      streamData={currentStream}
                      theme={memorialTheme}
                      donationsEnabled={
                        currentStream?.go_fund_me_enabled || false
                      }
                      streamProgram={streamProgram}
                      handleDownloadProgram={handleDownloadProgram}
                      downloadingProgram={downloadingProgram}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* ── Bottom tabs: Stream Details / Program ── */}
          <motion.div variants={itemVariants}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              }}
            >
              <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
                <Tabs
                  value={bottomTab}
                  onChange={(_, v) => setBottomTab(v)}
                  sx={{
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.9rem",
                    },
                  }}
                >
                  <Tab label="Stream Details" />
                  <Tab label="Program" />
                </Tabs>
              </Box>

              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                {/* ── Stream Details ── */}
                {bottomTab === 0 && (
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 8 }}>
                      <Stack spacing={1} sx={{ mb: 2.5 }}>
                        {[
                          {
                            icon: <CalendarTodayIcon sx={{ fontSize: 16 }} />,
                            label: "Date:",
                            value: eventDate,
                          },
                          {
                            icon: <AccessTimeIcon sx={{ fontSize: 16 }} />,
                            label: "Time:",
                            value: eventTime,
                          },
                          {
                            icon: <LocationOnIcon sx={{ fontSize: 16 }} />,
                            label: "Location:",
                            value: eventVenue,
                          },
                        ]
                          .filter((row) => row.value)
                          .map((row) => (
                            <Box
                              key={row.label}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box sx={{ color: memorialTheme.primary }}>
                                {row.icon}
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, minWidth: 80 }}
                              >
                                {row.label}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {row.value}
                              </Typography>
                            </Box>
                          ))}
                      </Stack>

                      <Divider sx={{ mb: 2 }} />

                      {/* {currentStream?.booking?.description && (
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 2,
                            lineHeight: 1.8,
                            color: "text.secondary",
                          }}
                        >
                          {currentStream.booking.description}
                        </Typography>
                      )} */}

                      {/* Speakers header row with Add Speaker button */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mt: 3,
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ color: memorialTheme.primary }}
                        >
                          Speakers
                        </Typography>
                        {isAdmin && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PersonAdd />}
                            onClick={() => setSpeakerDialogOpen(true)}
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              borderColor: memorialTheme.primary,
                              color: memorialTheme.primary,
                              "&:hover": {
                                borderColor: memorialTheme.primary,
                                bgcolor: alpha(memorialTheme.primary, 0.06),
                              },
                            }}
                          >
                            Add Speaker
                          </Button>
                        )}
                      </Box>

                      {/* Speakers grid — 4 per row */}
                      <Grid container spacing={1.5}>
                        {speakers.length === 0 ? (
                          <Grid size={{ xs: 12 }}>
                            <Typography variant="body2" color="text.secondary">
                              No speakers added yet.
                            </Typography>
                          </Grid>
                        ) : (
                          speakers.map((sp) => (
                            <Grid size={{ xs: 6, sm: 3, md: 3 }} key={sp.id}>
                              <SpeakerCard
                                name={sp.name}
                                role={sp.role ?? ""}
                                image={sp.image}
                                onDelete={
                                  isAdmin
                                    ? () => handleDeleteSpeaker(sp.id)
                                    : undefined
                                }
                              />
                            </Grid>
                          ))
                        )}
                      </Grid>
                    </Grid>

                    {/* Right column */}
                    <Grid size={{ xs: 12, md: 4 }}>
                      <LivePreviewCard stream={currentStream} />
                    </Grid>
                  </Grid>
                )}

                {/* ── Program tab ── */}
                {bottomTab === 1 && (
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, color: memorialTheme.primary }}
                    >
                      Event Program
                    </Typography>
                    <Stack spacing={2}>
                      {streams.map((stream, i) => {
                        const isActive = activeStreamIndex === i;
                        const time =
                          stream.start && stream.end
                            ? `${stream.start} – ${stream.end}`
                            : (stream.date ?? "");
                        const thumb =
                          stream.stream_thumbnail_background_image ||
                          (stream.youtube_stream_link
                            ? (() => {
                                const m = stream.youtube_stream_link.match(
                                  /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
                                );
                                return m
                                  ? `https://img.youtube.com/vi/${m[1]}/mqdefault.jpg`
                                  : null;
                              })()
                            : null);

                        return (
                          <Box
                            key={stream.id ?? i}
                            onClick={() => {
                              setActiveStreamIndex(i);
                              window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                            sx={{
                              display: "flex",
                              gap: 2,
                              p: 2,
                              borderRadius: 2,
                              cursor: "pointer",
                              bgcolor: isActive
                                ? alpha(memorialTheme.primary, 0.08)
                                : alpha(memorialTheme.primary, 0.03),
                              border: isActive
                                ? `1px solid ${alpha(memorialTheme.primary, 0.4)}`
                                : `1px solid ${alpha(memorialTheme.primary, 0.1)}`,
                              transition: "all 0.18s",
                              "&:hover": {
                                bgcolor: alpha(memorialTheme.primary, 0.08),
                              },
                            }}
                          >
                            {thumb && (
                              <Box
                                component="img"
                                src={thumb}
                                sx={{
                                  width: 100,
                                  height: 70,
                                  borderRadius: 1.5,
                                  objectFit: "cover",
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            <Box>
                              <Chip
                                label={`Session ${i + 1}`}
                                size="small"
                                sx={{
                                  bgcolor: memorialTheme.primary,
                                  color: "#fff",
                                  mb: 0.5,
                                  height: 20,
                                  fontSize: "0.65rem",
                                }}
                              />
                              <Typography variant="subtitle2">
                                {stream.title || `Session ${i + 1}`}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {time}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </Container>

      {/* Mobile share drawer */}
      <ShareDrawer
        open={shareDrawerOpen}
        onClose={() => setShareDrawerOpen(false)}
        streamData={currentStream}
      />

      {/* ── Add Speaker Dialog ── */}
      <Dialog
        open={speakerDialogOpen}
        onClose={handleCloseSpeakerDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Add Speaker</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              required
              fullWidth
              value={speakerForm.name}
              onChange={(e) => handleSpeakerFormChange("name", e.target.value)}
              variant="outlined"
            />
            <TextField
              label="Role"
              fullWidth
              value={speakerForm.role}
              onChange={(e) => handleSpeakerFormChange("role", e.target.value)}
              variant="outlined"
            />
            <TextField
              label="Email"
              fullWidth
              type="email"
              value={speakerForm.email}
              onChange={(e) => handleSpeakerFormChange("email", e.target.value)}
              variant="outlined"
            />
            <TextField
              label="Phone Number"
              fullWidth
              value={speakerForm.phone_number}
              onChange={(e) =>
                handleSpeakerFormChange("phone_number", e.target.value)
              }
              variant="outlined"
            />
            {/* Image upload */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                Speaker Image
              </Typography>
              <input
                type="file"
                accept="image/*"
                id="speaker-image-upload"
                hidden
                onChange={handleSpeakerImageChange}
              />
              <label htmlFor="speaker-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  sx={{ textTransform: "none" }}
                >
                  {speakerForm.image ? speakerForm.image.name : "Choose Image"}
                </Button>
              </label>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseSpeakerDialog}
            disabled={submittingSpeaker}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddSpeaker}
            disabled={submittingSpeaker}
            startIcon={
              submittingSpeaker ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PersonAdd />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              bgcolor: memorialTheme.primary,
              "&:hover": { bgcolor: alpha(memorialTheme.primary, 0.85) },
            }}
          >
            {submittingSpeaker ? "Adding..." : "Add Speaker"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
